import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { LandingPage } from './pages/LandingPage'
import { LoginPage } from './pages/LoginPage'
import { ChatPage } from './pages/ChatPage'
import { AnalyzingPage } from './pages/AnalyzingPage'
import { SettingsPage } from './pages/SettingsPage'
import { HistoryPage } from './pages/HistoryPage'
import { ConflictsPage } from './pages/ConflictsPage'
import { AddContextPage } from './pages/AddContextPage'
import { DocumentsPage } from './pages/DocumentsPage'
import { DocumentDetailPage } from './pages/DocumentDetailPage'
import { PackagesPage } from './pages/PackagesPage'
import { ArtifactsPage } from './pages/ArtifactsPage'
import { ContextPage } from './pages/ContextPage'
import { LegacyDomainDetailPage } from './pages/LegacyDomainDetailPage'
import { DomainDetailPage } from './pages/DomainDetailPage'
import { OnboardingPage } from './pages/OnboardingPage'
import { OverviewPage } from './pages/OverviewPage'
import { FillGapsPage } from './pages/FillGapsPage'
import { ExportPage } from './pages/ExportPage'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { AppShell } from './components/layout/AppShell'
import { ScrollToTop } from './components/layout/ScrollToTop'
import { ThemeProvider } from './components/theme/ThemeProvider'
import { useAuth } from './hooks/useAuth'

function App() {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <ThemeProvider defaultTheme="dark">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Public */}
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/overview" replace /> : <LoginPage />}
          />

          {/* Onboarding — first-run setup, sits OUTSIDE AppShell. */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />

          {/* Authenticated shell */}
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            {/* Phase 3+ canonical surface */}
            <Route path="/overview" element={<OverviewPage />} />
            {/* /domains (no key) renders the canonical domain grid, which
                today lives on the Overview page. Avoids a dead nav link
                bouncing through the catch-all to /context. */}
            <Route path="/domains" element={<Navigate to="/overview" replace />} />
            <Route path="/domains/:domainKey" element={<DomainDetailPage />} />
            <Route path="/fill-gaps" element={<FillGapsPage />} />
            <Route path="/export" element={<ExportPage />} />

            {/* Context Engine */}
            <Route path="/context" element={<ContextPage />} />
            <Route path="/context/domains/:domain" element={<LegacyDomainDetailPage />} />
            <Route path="/context/history" element={<HistoryPage />} />
            <Route path="/context/conflicts" element={<ConflictsPage />} />

            {/* Chat */}
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/chat/:sessionId" element={<ChatPage />} />

            {/* Input */}
            <Route path="/context/add" element={<AddContextPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route
              path="/documents/:documentId"
              element={<DocumentDetailPage />}
            />

            {/* Output */}
            <Route path="/packages" element={<PackagesPage />} />
            <Route path="/artifacts" element={<ArtifactsPage />} />

            {/* Analyzing */}
            <Route path="/analyzing/:documentId" element={<AnalyzingPage />} />

            {/* Settings */}
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Backwards-compat redirects */}
          <Route path="/interview" element={<Navigate to="/context/add" replace />} />
          <Route path="/app/settings" element={<Navigate to="/settings" replace />} />
          <Route path="/app/*" element={<Navigate to="/context" replace />} />

          {/* Catch all */}
          <Route
            path="*"
            element={<Navigate to={isAuthenticated ? "/overview" : "/"} replace />}
          />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  )
}

export default App
